<div class="workshop-detail" *transloco="let t; prefix: 'assetSettings'">
  @if (isLoading || isSaving || isDeleting) {
    <div class="detail-loading">
      <div class="spinner-border" role="status" [attr.aria-label]="t('common.loadingAria')"></div>
    </div>
  } @else {
  <!-- Header -->
  <div class="workshop-detail__header">
    <h3>{{ mode === 'edit' ? workshop?.name : t('workshops.detail.addNewTitle') }}</h3>
    @if (mode === 'edit') {
      <div class="workshop-detail__actions">
        <button type="button" class="btn-icon" [attr.aria-label]="t('common.edit')" (click)="enableEditing()">
          <i class="ph ph-pencil-simple"></i>
        </button>
        <button type="button" class="btn-icon" [attr.aria-label]="t('common.delete')" (click)="onDelete()">
          <i class="ph ph-trash"></i>
        </button>
      </div>
    }
  </div>

  <!-- Form -->
  <div class="workshop-detail__form" [formGroup]="form">
    <!-- Row 1: Account + External or In-House -->
    <div class="form-row">
      <div class="form-group half-width">
        <label>{{ t('common.account') }}<span class="required">*</span></label>
        @if (isEditing) {
          <app-account-selector
            (accountChanged)="onSelectAccount($event)"
            [isFilter]="false"
            [preSelectedAccountId]="workshop?.accountId ?? null">
          </app-account-selector>
        } @else {
          <input type="text" class="form-control" [value]="workshop?.account || ''" disabled>
        }
      </div>
      <div class="form-group half-width">
        <label>{{ t('common.externalOrInHouse') }}<span class="required">*</span></label>
        <span ngbDropdown class="type-dropdown" [autoClose]="true">
          <button type="button" class="btn form-control dropdown-toggle type-dropdown__trigger" ngbDropdownToggle [disabled]="!isEditing">
            <div class="d-flex flex-row justify-content-between dropdown-inner">
              <span>{{ selectedType }}</span>
              <i class="ph ph-caret-down"></i>
            </div>
          </button>
          <div ngbDropdownMenu>
            <div class="d-flex flex-column">
              @for (type of workshopTypes; track type) {
                <button class="btn btn-outline" (click)="onTypeSelect(type)" ngbDropdownItem>{{ workshopTypeLabels[type] }}</button>
              }
            </div>
          </div>
        </span>
      </div>
    </div>

    <!-- Row 2: Workshop Name + Location -->
    <div class="form-row">
      <div class="form-group half-width">
        <label>{{ t('workshops.detail.workshopName') }}<span class="required">*</span></label>
        <input type="text" class="form-control" formControlName="name"
          [placeholder]="t('workshops.detail.workshopName')"
          [class.is-invalid]="isFieldInvalid('name')">
      </div>
      <div class="form-group half-width">
        <label>{{ t('common.location') }}<span class="required">*</span></label>
        <div class="input-with-icon">
          <i class="ph ph-magnifying-glass"></i>
          <input #locationInput type="text" class="form-control" formControlName="location"
            autocomplete="off"
            [placeholder]="t('workshops.detail.searchAddressPlaceholder')"
            [class.is-invalid]="isFieldInvalid('location')">
        </div>
      </div>
    </div>

    <!-- Row 3: Email + Phone Number -->
    <div class="form-row">
      <div class="form-group half-width">
        <label>{{ t('common.email') }}<span class="required">*</span></label>
        <input type="email" class="form-control" formControlName="email"
          [placeholder]="t('workshops.detail.emailPlaceholder')"
          [class.is-invalid]="isFieldInvalid('email')">
      </div>
      <div class="form-group half-width">
        <label>{{ t('common.phoneNumber') }}<span class="required">*</span></label>
        <input type="tel" class="form-control" formControlName="phoneNumber"
          placeholder="012 345 6789"
          [class.is-invalid]="isFieldInvalid('phoneNumber')">
      </div>
    </div>

    <!-- Row 4: Labour Rate (in-house workshops only) -->
    @if (isInHouse) {
      <div class="form-row">
        <div class="form-group half-width labour-rate-field">
          <label>
            {{ t('workshops.detail.labourRateCurrency') }}
            <i
              class="ph ph-info info-icon"
              [ngbTooltip]="t('workshops.detail.labourRateTooltip')"
              placement="top"
              triggers="hover"
            ></i>
          </label>
          <div class="labour-rate-group" [class.disabled]="!isEditing">
            <div class="currency-selector" (click)="toggleCurrencyDropdown($event)">
              <app-country-flag [countryCode]="selectedCurrency?.code || ''" [name]="selectedCurrency?.name || ''"
                [size]="18" [rounded]="true"></app-country-flag>
              <span class="currency-code">{{ selectedCurrency?.code || t('workshops.detail.selectCurrency') }}</span>
              <i class="ph ph-caret-down"></i>
            </div>
            <input type="number" class="labour-rate-group__input" formControlName="labourRate"
              min="0" step="0.01" placeholder="0.00">
            <span class="labour-rate-group__suffix">{{ 'assetShared.perHour' | transloco }}</span>

            @if (showCurrencyDropdown) {
              <div class="currency-dropdown-menu" (click)="$event.stopPropagation()">
                <div class="currency-search-field">
                  <i class="ph ph-magnifying-glass"></i>
                  <input type="text" class="currency-search-input" [placeholder]="t('workshops.detail.currencySearchPlaceholder')"
                    [(ngModel)]="currencySearchTerm" [ngModelOptions]="{ standalone: true }"
                    (ngModelChange)="filterCurrencies()">
                </div>
                <ul class="currency-list">
                  @if (isCurrenciesLoading) {
                    <li class="no-results">{{ t('workshops.detail.loadingCurrencies') }}</li>
                  } @else {
                    @for (currency of filteredCurrencies; track currency.id) {
                      <li class="currency-item" [class.selected]="selectedCurrency?.id === currency.id"
                        (click)="selectCurrency(currency)">
                        <div class="currency-item__info">
                          <app-country-flag [countryCode]="currency.code" [name]="currency.name"
                            [size]="18" [rounded]="true"></app-country-flag>
                          <span class="currency-item__code">{{ currency.code }}</span>
                          <span class="currency-item__name">{{ currency.name }}</span>
                        </div>
                        <span class="currency-item__radio" [class.checked]="selectedCurrency?.id === currency.id"></span>
                      </li>
                    }
                    @if (filteredCurrencies.length === 0) {
                      <li class="no-results">{{ t('workshops.detail.noCurrenciesFound') }}</li>
                    }
                  }
                </ul>
              </div>
            }
          </div>
        </div>
      </div>
    }
  </div>

  <!-- Map -->
  <div class="workshop-detail__map">
    <google-map [height]="'100%'" width="100%" [options]="mapOptions"
      (mapInitialized)="onMapInit($event)"
      (mapClick)="onMapClick($event)">

      <!-- Circle geofence -->
      @if (showCircle) {
        <map-circle [options]="circleOptions"
          (radiusChanged)="onCircleEdited()"
          (centerChanged)="onCircleEdited()">
        </map-circle>
      }

      <!-- Rectangle geofence -->
      @if (showRectangle && rectangleBounds) {
        <map-rectangle [bounds]="rectangleBounds" [options]="rectangleOptions"
          (boundsChanged)="onRectangleEdited()">
        </map-rectangle>
      }

      <!-- Custom polygon geofence -->
      @if (showPolygon && polygonPaths.length >= 3) {
        <map-polygon [paths]="polygonPaths" [options]="polygonOptions"></map-polygon>
      }

      <!-- Custom drawing point markers -->
      @if (isDrawingCustom) {
        @for (point of customPoints; track $index) {
          <map-marker [position]="point" [options]="customPointMarkerOptions"></map-marker>
        }
      }

      @if (isDrawingCustom && customPoints.length >= 2) {
        <map-polyline
          [path]="customPoints"
          [options]="customPolylineOptions">
        </map-polyline>
      }

      <!-- Cursor-following line during custom drawing -->
      @if (isDrawingCustom && customPoints.length >= 1 && cursorPosition) {
        <map-polyline
          [path]="cursorPolylinePath"
          [options]="cursorPolylineOptions">
        </map-polyline>
      }
    </google-map>

    <!-- Geofence Shape Selector -->
    @if (isEditing) {
      <div class="geofence-shape-selector">
        <div class="geofence-shape-selector__title"> {{ t('workshops.geofence.title') }} </div>
    
        <div class="geofence-shape-selector__options">
          <button type="button" class="shape-btn" [class.shape-btn--active]="activeGeofenceType === 'rectangle'" (click)="onShapeSelect('rectangle')">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 4C14 2.89733 13.1027 2 12 2C11.132 2 10.3993 2.55867 10.1233 3.33333H5.87667C5.60067 2.55867 4.868 2 4 2C2.89733 2 2 2.89733 2 4C2 4.868 2.55933 5.60067 3.33333 5.87667V10.1227C2.55933 10.3993 2 11.132 2 12C2 13.1027 2.89733 14 4 14C4.868 14 5.60067 13.4413 5.87667 12.6667H10.1227C10.3987 13.4413 11.132 14 11.9993 14C13.102 14 13.9993 13.1027 13.9993 12C13.9993 11.132 13.44 10.3993 12.666 10.1227V5.87733C13.4407 5.60067 14 4.868 14 4ZM12 3.33333C12.368 3.33333 12.6667 3.63267 12.6667 4C12.6667 4.36733 12.368 4.66667 12 4.66667C11.632 4.66667 11.3333 4.36733 11.3333 4C11.3333 3.63267 11.632 3.33333 12 3.33333ZM10.1233 11.3333H5.87667C5.676 10.7687 5.232 10.324 4.66667 10.1227V5.87733C5.232 5.676 5.676 5.23133 5.87667 4.66667H10.1227C10.324 5.23133 10.768 5.676 11.3333 5.87667V10.1227C10.768 10.324 10.324 10.7687 10.1233 11.3333ZM4 3.33333C4.368 3.33333 4.66667 3.63267 4.66667 4C4.66667 4.36733 4.368 4.66667 4 4.66667C3.632 4.66667 3.33333 4.36733 3.33333 4C3.33333 3.63267 3.632 3.33333 4 3.33333ZM4 12.6667C3.632 12.6667 3.33333 12.3673 3.33333 12C3.33333 11.6327 3.632 11.3333 4 11.3333C4.368 11.3333 4.66667 11.6327 4.66667 12C4.66667 12.3673 4.368 12.6667 4 12.6667ZM12 12.6667C11.632 12.6667 11.3333 12.3673 11.3333 12C11.3333 11.6327 11.632 11.3333 12 11.3333C12.368 11.3333 12.6667 11.6327 12.6667 12C12.6667 12.3673 12.368 12.6667 12 12.6667Z" fill="currentColor"/>
            </svg>              
            <span>{{ t('workshops.geofence.square') }}</span>
          </button>
    
          <button type="button" class="shape-btn" [class.shape-btn--active]="activeGeofenceType === 'circle'" (click)="onShapeSelect('circle')">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.9986 6.03337C12.4533 4.65737 11.3459 3.54937 9.96927 3.00404C9.81127 2.05871 8.9926 1.33337 8.0026 1.33337C7.0126 1.33337 6.19394 2.05871 6.03594 3.00404C4.65927 3.54937 3.55194 4.65671 3.0066 6.03337C2.06127 6.19137 1.33594 7.01004 1.33594 8.00004C1.33594 8.99004 2.06127 9.80871 3.0066 9.96671C3.55194 11.3427 4.65927 12.4507 6.03594 12.996C6.19394 13.9414 7.0126 14.6667 8.0026 14.6667C8.9926 14.6667 9.81127 13.9414 9.96927 12.996C11.3453 12.4507 12.4533 11.3434 12.9986 9.96671C13.9439 9.80871 14.6693 8.99004 14.6693 8.00004C14.6693 7.01004 13.9439 6.19137 12.9986 6.03337ZM8.0026 2.66671C8.3706 2.66671 8.66927 2.96537 8.66927 3.33337C8.66927 3.37337 8.65394 3.40671 8.6466 3.44471C8.63527 3.51071 8.61927 3.57404 8.59127 3.63071C8.58194 3.64871 8.5686 3.66337 8.55727 3.68071C8.52594 3.73071 8.4926 3.78004 8.4506 3.82004C8.42394 3.84471 8.3906 3.86137 8.35994 3.88137C8.32394 3.90537 8.2906 3.93337 8.24994 3.95004C8.17327 3.98137 8.0906 4.00004 8.0026 4.00004C7.9146 4.00004 7.83194 3.98137 7.75527 3.95004C7.7146 3.93404 7.68127 3.90604 7.64527 3.88137C7.6146 3.86137 7.58127 3.84471 7.5546 3.82004C7.5126 3.78004 7.47927 3.73071 7.44794 3.68071C7.4366 3.66337 7.42327 3.64871 7.41394 3.63071C7.38594 3.57404 7.36994 3.51004 7.3586 3.44471C7.35127 3.40671 7.33594 3.37337 7.33594 3.33337C7.33594 2.96537 7.6346 2.66671 8.0026 2.66671ZM3.33594 7.33337C3.37594 7.33337 3.40927 7.34871 3.44727 7.35604C3.51327 7.36737 3.5766 7.38337 3.63327 7.41137C3.65127 7.42071 3.66594 7.43404 3.68327 7.44537C3.73327 7.47671 3.7826 7.51004 3.8226 7.55204C3.84727 7.57871 3.86394 7.61204 3.88394 7.64271C3.90794 7.67871 3.93594 7.71204 3.9526 7.75271C3.98394 7.82937 4.0026 7.91204 4.0026 8.00004C4.0026 8.08804 3.98394 8.17071 3.9526 8.24737C3.9366 8.28804 3.9086 8.32137 3.88394 8.35737C3.86394 8.38804 3.84727 8.42137 3.8226 8.44804C3.7826 8.49004 3.73327 8.52337 3.68327 8.55471C3.66594 8.56604 3.65127 8.57937 3.63327 8.58871C3.5766 8.61671 3.5126 8.63271 3.44727 8.64404C3.40927 8.65137 3.37594 8.66671 3.33594 8.66671C2.96794 8.66671 2.66927 8.36804 2.66927 8.00004C2.66927 7.63204 2.96794 7.33337 3.33594 7.33337ZM8.0026 13.3334C7.6346 13.3334 7.33594 13.0347 7.33594 12.6667C7.33594 12.6267 7.35127 12.5934 7.3586 12.5554C7.36994 12.4894 7.38594 12.426 7.41394 12.3694C7.42327 12.3514 7.4366 12.3367 7.44794 12.3194C7.47927 12.2694 7.5126 12.22 7.5546 12.18C7.58127 12.1554 7.6146 12.1387 7.64527 12.1187C7.68127 12.0947 7.7146 12.0667 7.75527 12.05C7.83194 12.0187 7.9146 12 8.0026 12C8.0906 12 8.17327 12.0187 8.24994 12.05C8.2906 12.066 8.32394 12.094 8.35994 12.1187C8.3906 12.1387 8.42394 12.1554 8.4506 12.18C8.4926 12.22 8.52594 12.2694 8.55727 12.3194C8.5686 12.3367 8.58194 12.3514 8.59127 12.3694C8.61927 12.426 8.63527 12.49 8.6466 12.5554C8.65394 12.5934 8.66927 12.6267 8.66927 12.6667C8.66927 13.0347 8.3706 13.3334 8.0026 13.3334ZM9.7246 11.6587C9.7206 11.6514 9.71327 11.646 9.70994 11.6394C9.5446 11.3654 9.31794 11.136 9.04594 10.968C9.00994 10.946 8.97327 10.928 8.93527 10.908C8.8326 10.8527 8.7246 10.808 8.61127 10.7714C8.5686 10.7574 8.52594 10.742 8.48194 10.7307C8.3266 10.6927 8.16794 10.6667 8.0026 10.6667C7.83727 10.6667 7.6786 10.6927 7.52394 10.7314C7.47994 10.7427 7.43727 10.7574 7.39394 10.772C7.28127 10.8087 7.17327 10.8534 7.0706 10.9087C7.03327 10.928 6.99594 10.9467 6.95994 10.9687C6.68794 11.1367 6.46127 11.366 6.29594 11.64C6.29194 11.6474 6.2846 11.6527 6.28127 11.6594C5.43594 11.258 4.74527 10.5674 4.3446 9.72271C4.35194 9.71871 4.35727 9.71137 4.36394 9.70804C4.63794 9.54271 4.86727 9.31604 5.03527 9.04404C5.05727 9.00804 5.07594 8.97071 5.09527 8.93337C5.1506 8.83071 5.19527 8.72271 5.23194 8.61004C5.24594 8.56671 5.26127 8.52404 5.2726 8.48004C5.30994 8.32404 5.33594 8.16537 5.33594 8.00004C5.33594 7.83471 5.30994 7.67604 5.27127 7.52137C5.25994 7.47737 5.24527 7.43471 5.2306 7.39204C5.19394 7.27871 5.14927 7.17071 5.09394 7.06804C5.07394 7.03004 5.05594 6.99337 5.03394 6.95737C4.86594 6.68537 4.6366 6.45871 4.3626 6.29337C4.35594 6.28937 4.3506 6.28204 4.34394 6.27804C4.74527 5.43271 5.43594 4.74204 6.2806 4.34137C6.2846 4.34804 6.29194 4.35337 6.29527 4.36071C6.4606 4.63471 6.68727 4.86404 6.95927 5.03204C6.99527 5.05404 7.0326 5.07271 7.06994 5.09204C7.17327 5.14737 7.2806 5.19204 7.39394 5.22871C7.4366 5.24271 7.47927 5.25804 7.52327 5.26937C7.6786 5.30737 7.8366 5.33337 8.0026 5.33337C8.1686 5.33337 8.3266 5.30737 8.48127 5.26871C8.52527 5.25737 8.56794 5.24271 8.6106 5.22804C8.72394 5.19137 8.83127 5.14671 8.9346 5.09137C8.97194 5.07204 9.00927 5.05337 9.04527 5.03137C9.31727 4.86337 9.54394 4.63404 9.70927 4.36004C9.71327 4.35271 9.7206 4.34737 9.72394 4.34071C10.5693 4.74204 11.2599 5.43271 11.6606 6.27737C11.6533 6.28137 11.6479 6.28871 11.6413 6.29204C11.3673 6.45737 11.1379 6.68404 10.9699 6.95604C10.9479 6.99204 10.9293 7.02937 10.9099 7.06671C10.8546 7.17004 10.8099 7.27737 10.7733 7.39071C10.7593 7.43337 10.7439 7.47604 10.7326 7.52004C10.6953 7.67604 10.6693 7.83404 10.6693 8.00004C10.6693 8.16604 10.6953 8.32404 10.7339 8.47871C10.7453 8.52271 10.7599 8.56537 10.7746 8.60804C10.8113 8.72137 10.8559 8.82871 10.9113 8.93204C10.9306 8.96937 10.9493 9.00671 10.9713 9.04271C11.1393 9.31471 11.3686 9.54137 11.6426 9.70671C11.6499 9.71071 11.6553 9.71804 11.6619 9.72137C11.2606 10.5674 10.5699 11.258 9.7246 11.6587ZM12.6693 8.66671C12.6293 8.66671 12.5959 8.65137 12.5579 8.64404C12.4919 8.63271 12.4286 8.61671 12.3719 8.58871C12.3539 8.57937 12.3393 8.56604 12.3219 8.55471C12.2719 8.52337 12.2226 8.49004 12.1826 8.44804C12.1579 8.42137 12.1413 8.38804 12.1213 8.35737C12.0973 8.32137 12.0693 8.28804 12.0526 8.24737C12.0213 8.17071 12.0026 8.08804 12.0026 8.00004C12.0026 7.91204 12.0213 7.82937 12.0526 7.75271C12.0686 7.71204 12.0966 7.67871 12.1213 7.64271C12.1413 7.61204 12.1579 7.57871 12.1826 7.55204C12.2226 7.51004 12.2719 7.47671 12.3219 7.44537C12.3393 7.43404 12.3539 7.42071 12.3719 7.41137C12.4286 7.38337 12.4926 7.36737 12.5579 7.35604C12.5959 7.34871 12.6293 7.33337 12.6693 7.33337C13.0373 7.33337 13.3359 7.63204 13.3359 8.00004C13.3359 8.36804 13.0373 8.66671 12.6693 8.66671Z" fill="currentColor"/>
            </svg>              
            <span>{{ t('workshops.geofence.circle') }}</span>
          </button>
    
          <button type="button" class="shape-btn" [class.shape-btn--active]="activeGeofenceType === 'custom'" (click)="onShapeSelect('custom')">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 10C11.878 10 11.7613 10.0147 11.6453 10.036L5.964 4.35467C5.98533 4.23933 6 4.12133 6 4C6 2.89733 5.10267 2 4 2C2.89733 2 2 2.89733 2 4C2 4.868 2.55933 5.60067 3.33333 5.87667V10.1227C2.55933 10.3993 2 11.132 2 12C2 13.1027 2.89733 14 4 14C4.868 14 5.60067 13.4413 5.87667 12.6667H10.1227C10.3987 13.4413 11.132 14 11.9993 14C13.102 14 13.9993 13.1027 13.9993 12C13.9993 10.8973 13.1027 10 12 10ZM4.66667 10.1227V5.87733C4.886 5.79867 5.086 5.68333 5.26267 5.53867L10.462 10.738C10.3167 10.9147 10.2013 11.114 10.1233 11.334H5.87667C5.676 10.7687 5.232 10.324 4.66667 10.1227ZM4 3.33333C4.368 3.33333 4.66667 3.63267 4.66667 4C4.66667 4.36733 4.368 4.66667 4 4.66667C3.632 4.66667 3.33333 4.36733 3.33333 4C3.33333 3.63267 3.632 3.33333 4 3.33333ZM4 12.6667C3.632 12.6667 3.33333 12.3673 3.33333 12C3.33333 11.6327 3.632 11.3333 4 11.3333C4.368 11.3333 4.66667 11.6327 4.66667 12C4.66667 12.3673 4.368 12.6667 4 12.6667ZM12 12.6667C11.632 12.6667 11.3333 12.3673 11.3333 12C11.3333 11.6327 11.632 11.3333 12 11.3333C12.368 11.3333 12.6667 11.6327 12.6667 12C12.6667 12.3673 12.368 12.6667 12 12.6667Z" fill="currentColor"/>
            </svg>              
            <span>{{ t('workshops.geofence.custom') }}</span>
          </button>
        </div>
    
        @if (activeGeofenceType !== 'custom' && (showCircle || showRectangle)) {
          <div class="geofence-size-control">
            <div class="geofence-size-control__top">
              <span>{{ t('workshops.geofence.size') }}</span>
              <span>{{ 'shared.units.metersValue' | transloco: { value: geofenceSizeMeters } }}</span>
            </div>
    
            <div class="geofence-size-control__slider-wrap">
              <input
                type="range"
                min="50"
                max="1000"
                step="10"
                class="geofence-size-control__slider"
                [value]="geofenceSizeMeters"
                [style.--range-percent]="geofenceSliderPercent + '%'"
                (input)="onGeofenceSizeChange($any($event.target).value)" />
            </div>
          </div>
        }
      </div>
    }

    <!-- Map Layers Toggle -->
    <div class="map-layers-control">
      <button type="button" class="layers-toggle-btn" (click)="toggleMapLayer()" [attr.aria-label]="currentMapType === 'roadmap' ? t('workshops.map.switchToSatellite') : t('workshops.map.switchToMap')">
        @if (layerPreviewUrl) {
          <img class="layers-toggle-btn__preview"
            [src]="layerPreviewUrl"
            [alt]="currentMapType === 'roadmap' ? t('workshops.detail.satelliteView') : t('workshops.detail.mapView')">
        }
        <span class="layers-toggle-btn__label">{{ currentMapType === 'roadmap' ? t('workshops.map.satellite') : t('workshops.map.map') }}</span>
      </button>
    </div>
  </div>

  <!-- Footer -->
  <div class="workshop-detail__footer">
    <button type="button" class="btn btn-cancel" [disabled]="isCancelDisabled" (click)="onCancel()">{{ 'common.cancel' | transloco }}</button>
    <button type="button" class="btn btn-save" (click)="onSave()" [disabled]="isSaveDisabled">{{ t('common.save') }}</button>
  </div>
  }
</div>